import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'

import { Offer } from '../../rest/types/Offer'
import { numberOfDays } from '../../helpers/OfferHelpers'
import { actions, selectors } from '../../redux'
import Router, { routes } from '../../routes/Router'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { BannerOfferProps } from '../../components/BannerOffer'
import { ActionButtonVariant } from '../../components/ActionButton/styled'

const useOfferBannerProps = (data: Offer): BannerOfferProps => {
  const { t } = useTranslation()
  const isLogin = useSelector(selectors.auth.isConnected)
  const isMember = useSelector(selectors.auth.isSubscriptionActive)
  const dispatch = useDispatch()
  const reservedOffersIDs = useSelector(
    selectors.reservations.reservedOffersIDs
  )
  const hasAccessToStore = useSelector(selectors.auth.hasAccessToStore)
  const user = useSelector(selectors.auth.user)

  const card: any = data?.card?.[0]

  const isBooked = reservedOffersIDs?.includes(data?.id)
  const isExpired = card?.endDate && dayjs(card?.endDate).isBefore()

  const [reservation, reservationHandler] = useServiceRequest(
    selectors.reservations.toggleReservation,
    actions.reservations.toggleReservationRequest,
    actions.reservations.toggleReservationReset
  )

  // connected users without store access are sent to the access request form
  // instead of the login modal
  const requestAccessOrLoginButton = {
    text: isLogin ? t('request_access_button_text') : t('login_button_text'),
    variant: card?.link?.variant,
    target: card?.link?.target,
    ...(isLogin
      ? {
          href: t('form_path_request_access_central'),
        }
      : {
          onClick: () => {
            dispatch(actions.modals.setLoginModalIsOpen(true))
          },
        }),
  }

  let offerBannerProps: BannerOfferProps
  if (card?.__component === 'offer.type-1') {
    offerBannerProps = {
      ...((!isLogin || !hasAccessToStore) && {
        titleNotConnected: t('title_banner_offer_not_connected'),
        textNotConnected: t('text_banner_offer_not_connected'),
      }),
      ...(isLogin && hasAccessToStore && isExpired
        ? {
            titleNotConnected: t('pre_reservation_expired_label'),
            textNotConnected: t('pre_reservation_expired_text'),
          }
        : {
            ...(isLogin &&
              hasAccessToStore && {
                dateLabel: card?.dateLabel,
                date: `${t('symbol_before_day_abbreviated')} ${numberOfDays(
                  card?.endDate
                )} ${t('day_abbreviated')}`,
                goalsLabel: card?.goalsLabel,
                goals: card?.goals,
              }),
            textBlockRight:
              isLogin && isMember && hasAccessToStore
                ? isBooked
                  ? t('pre_reservation_done_label')
                  : t('pre_reservation_label')
                : isLogin && hasAccessToStore && !isMember
                ? t('subscription_label')
                : '',
            button: {
              text:
                isLogin && isMember && hasAccessToStore
                  ? isBooked
                    ? t('participation_button_cancel_text')
                    : t('participation_button_text')
                  : isLogin && hasAccessToStore
                  ? t('subscription_button_text')
                  : isLogin
                  ? t('request_access_button_text')
                  : t('login_button_text'),
              variant: isBooked
                ? ActionButtonVariant.quaternary
                : card?.link?.variant,
              target: card?.link?.target,
              ...(isLogin && isMember && hasAccessToStore
                ? {
                    onClick: () => {
                      reservationHandler({
                        offer: data?.id ?? '',
                        userId: user?.id,
                        offerCategories: data?.categories,
                      })
                    },
                    autoIcon: true,
                    isPending: reservation.pending,
                  }
                : isLogin && hasAccessToStore
                ? {
                    href: Router.getRouteUrl(routes.subscription),
                  }
                : isLogin
                ? {
                    href: t('form_path_request_access_central'),
                  }
                : {
                    onClick: () =>
                      dispatch(actions.modals.setLoginModalIsOpen(true)),
                  }),
              ...(isLogin &&
                hasAccessToStore &&
                !isMember && {
                  href: Router.getRouteUrl(routes.subscription),
                }),
            },
          }),
    }
  } else if (card?.__component === 'offer.type-2') {
    offerBannerProps = {
      ...(isLogin &&
        hasAccessToStore && {
          priceLabel: card?.priceLabel,
          price: card?.price,
          publicPriceLabel: card?.publicPriceLabel,
          publicPrice: card?.publicPrice,
          priceExposant: card?.priceExposant,
          discount: card?.discount,
        }),
      ...((!isLogin || !hasAccessToStore) && {
        titleNotConnected: t('title_banner_offer_not_connected'),
        textNotConnected: t('text_banner_offer_not_connected'),
      }),
      textBlockRight:
        isLogin && isMember && hasAccessToStore
          ? t('voucher_code_label')
          : !isLogin || !hasAccessToStore
          ? ''
          : t('subscription_label'),
      ...((!isLogin || !hasAccessToStore) && {
        button: requestAccessOrLoginButton,
      }),
      ...(isLogin &&
        hasAccessToStore &&
        !isMember && {
          button: {
            text: !isLogin
              ? t('login_button_text')
              : t('subscription_button_text'),
            href: Router.getRouteUrl(routes.subscription),
            variant: card?.link?.variant,
            target: card?.link?.target,
          },
        }),
      voucherCode:
        isLogin && isMember && hasAccessToStore ? card?.code : undefined,
    }
  } else if (card?.__component === 'offer.type-3') {
    offerBannerProps = {
      ...(isLogin &&
        hasAccessToStore && {
          priceLabel: card?.priceLabel,
          price: card?.price,
          priceExposant: card?.priceExposant,
          discountLabel: card?.discountLabel,
          discount: card?.discount,
          discountLegend: card?.discountLegend,
        }),
      ...((!isLogin || !hasAccessToStore) && {
        titleNotConnected: t('title_banner_offer_not_connected'),
        textNotConnected: t('text_banner_offer_not_connected'),
      }),
      textBlockRight:
        isLogin && isMember && hasAccessToStore
          ? t('voucher_code_label')
          : !isLogin || !hasAccessToStore
          ? ''
          : t('subscription_label'),
      ...((!isLogin || !hasAccessToStore) && {
        button: requestAccessOrLoginButton,
      }),
      ...(isLogin &&
        hasAccessToStore &&
        !isMember && {
          button: {
            text: !isLogin
              ? t('login_button_text')
              : t('subscription_button_text'),
            href: Router.getRouteUrl(routes.subscription),
            variant: card?.link?.variant,
            target: card?.link?.target,
          },
        }),
      voucherCode:
        isLogin && isMember && hasAccessToStore ? card?.code : undefined,
    }
  } else {
    offerBannerProps = {
      ...(isLogin &&
        hasAccessToStore &&
        isMember && {
          discoverOfferLabel: card?.label,
        }),
      ...((!isLogin || !hasAccessToStore) && {
        titleNotConnected: t('title_banner_offer_not_connected'),
        textNotConnected: t('text_banner_offer_not_connected'),
      }),
      textBlockRight:
        isLogin && isMember && hasAccessToStore
          ? t('voucher_code_label')
          : !isLogin || !hasAccessToStore
          ? ''
          : isLogin && hasAccessToStore && !isMember
          ? t('subscription_label')
          : t('subscription_label'),
      ...((!isLogin || !hasAccessToStore) && {
        button: requestAccessOrLoginButton,
      }),
      ...(isLogin &&
        hasAccessToStore &&
        !isMember && {
          button: {
            text: !isLogin
              ? t('login_button_text')
              : t('subscription_button_text'),
            href: Router.getRouteUrl(routes.subscription),
            variant: card?.link?.variant,
            target: card?.link?.target,
          },
        }),
      voucherCode:
        isLogin && isMember && hasAccessToStore ? card?.code : undefined,
    }
  }

  return offerBannerProps
}

export default useOfferBannerProps
